import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Loader,
  NumberInput,
  Paper,
  PasswordInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconInfoCircle,
  IconMail,
  IconSend,
} from '@tabler/icons-react';
import type { MailerSettingsDto } from '@expeditor/shared';
import { mailerApi } from '../../shared/api/mailer';
import { useAuthStore } from '../../shared/auth/auth.store';

type FormState = {
  enabled: boolean;
  host: string;
  port: number | string;
  secure: boolean;
  allowSelfSigned: boolean;
  username: string;
  password: string;
  mailFrom: string;
};

const emptyForm: FormState = {
  enabled: false,
  host: '',
  port: 587,
  secure: false,
  allowSelfSigned: false,
  username: '',
  password: '',
  mailFrom: '',
};

function dtoToForm(dto: MailerSettingsDto): FormState {
  return {
    enabled: dto.enabled,
    host: dto.host ?? '',
    port: dto.port ?? 587,
    secure: dto.secure,
    allowSelfSigned: dto.allowSelfSigned,
    username: dto.username ?? '',
    password: '',
    mailFrom: dto.mailFrom ?? '',
  };
}

export function MailerSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const [dto, setDto] = useState<MailerSettingsDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Тест
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    mailerApi
      .getSettings()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setForm(dtoToForm(d));
        setTestTo(user?.email ?? '');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      // password: '' — не меняем (пустая строка из инпута = ничего не делать).
      // Бэк интерпретирует undefined как «не трогать», пустое строку — как «стереть».
      const payload = {
        enabled: form.enabled,
        host: form.host,
        port: Number(form.port) || null,
        secure: form.secure,
        allowSelfSigned: form.allowSelfSigned,
        username: form.username,
        password: form.password === '' ? undefined : form.password,
        mailFrom: form.mailFrom,
      };
      const updated = await mailerApi.updateSettings(payload);
      setDto(updated);
      setForm(dtoToForm(updated));
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await mailerApi.sendTest(testTo);
      setTestResult(r);
      // Перечитываем DTO чтобы подтянуть lastTestedAt
      const fresh = await mailerApi.getSettings();
      setDto(fresh);
    } catch (err) {
      setTestResult({
        ok: false,
        error: err instanceof Error ? err.message : 'Ошибка',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Box>
      <Title order={3} mb="md">
        Настройки SMTP
      </Title>

      <Paper withBorder p="lg" radius="md" mb="md">
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Group justify="space-between">
              <Switch
                label="Отправка писем включена"
                description="Если выключено — система не шлёт ничего, только пишет в Notification со status=FAILED"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.currentTarget.checked })}
              />
              {dto?.passwordSet ? (
                <Badge color="green" variant="light">
                  пароль сохранён
                </Badge>
              ) : (
                <Badge color="gray" variant="light">
                  пароль не задан
                </Badge>
              )}
            </Group>

            <Divider label="SMTP-сервер" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput
                  label="Хост"
                  placeholder="smtp.rkmsoft.ru"
                  value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.currentTarget.value })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2 }}>
                <NumberInput
                  label="Порт"
                  value={form.port}
                  onChange={(v) => setForm({ ...form, port: v as number })}
                  min={1}
                  max={65535}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2 }}>
                <Switch
                  label="SMTPS (TLS)"
                  description="465 — да, 587/25 — нет"
                  checked={form.secure}
                  onChange={(e) => setForm({ ...form, secure: e.currentTarget.checked })}
                  mt={20}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Switch
                  label="Принимать самоподписанные TLS-сертификаты"
                  description="Включите, если SMTP-сервер использует self-signed сертификат (внутренний сервер RKM). Иначе nodemailer отвергнет соединение с ошибкой «self-signed certificate»."
                  checked={form.allowSelfSigned}
                  onChange={(e) => setForm({ ...form, allowSelfSigned: e.currentTarget.checked })}
                />
              </Grid.Col>
            </Grid>

            <Divider label="Аутентификация" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Логин"
                  placeholder="no-reply@rkmsoft.ru"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.currentTarget.value })}
                  autoComplete="off"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <PasswordInput
                  label="Пароль"
                  description={dto?.passwordSet ? 'Оставьте пустым чтобы не менять' : 'Задайте при первом сохранении'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.currentTarget.value })}
                  autoComplete="new-password"
                />
              </Grid.Col>
            </Grid>

            <Divider label="Отправитель" />
            <TextInput
              label="Адрес отправителя (MAIL FROM)"
              description="С этого адреса будут приходить письма пользователям"
              placeholder="no-reply@rkmsoft.ru"
              type="email"
              required={form.enabled}
              value={form.mailFrom}
              onChange={(e) => setForm({ ...form, mailFrom: e.currentTarget.value })}
            />

            {error && (
              <Alert color="red" icon={<IconInfoCircle size={16} />}>
                {error}
              </Alert>
            )}
            {savedAt && (
              <Alert color="green" icon={<IconDeviceFloppy size={16} />}>
                Сохранено в {savedAt.toLocaleTimeString('ru-RU')}.
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                type="submit"
                loading={saving}
                leftSection={<IconDeviceFloppy size={16} />}
              >
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="xs">
          Тест отправки
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Шлёт тестовое письмо с текущими настройками. Полезно сразу проверить
          что SMTP отвечает.
        </Text>
        <Group align="flex-end" gap="xs">
          <TextInput
            label="Кому"
            placeholder="вы@вашpost.ru"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            onClick={sendTest}
            loading={testing}
            disabled={!testTo}
            leftSection={<IconSend size={16} />}
          >
            Отправить тест
          </Button>
        </Group>
        {testResult && (
          <Alert
            mt="md"
            color={testResult.ok ? 'green' : 'red'}
            icon={<IconMail size={16} />}
          >
            {testResult.ok
              ? 'Тестовое письмо отправлено успешно. Проверьте входящие.'
              : `Ошибка: ${testResult.error}`}
          </Alert>
        )}
        {dto?.lastTestedAt && (
          <Text size="xs" c="dimmed" mt="xs">
            Последний тест: {new Date(dto.lastTestedAt).toLocaleString('ru-RU')} —{' '}
            <strong>{dto.lastTestStatus === 'ok' ? 'успех' : 'ошибка'}</strong>
            {dto.lastTestError ? ` (${dto.lastTestError})` : ''}
          </Text>
        )}
      </Paper>
    </Box>
  );
}
